import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Package, Heart, LogOut, ChevronRight, Save, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = '';

export default function AccountPage() {
  const { user, login, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    phone: '',
    address: '',
    wilaya: ''
  });
  const [wilayas, setWilayas] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchWilayas = async () => {
      try {
        const response = await fetch(`${API_URL}/api/wilayas`);
        if (response.ok) {
          const data = await response.json();
          setWilayas(data.wilayas || []);
        }
      } catch (error) {
        console.error('Error fetching wilayas:', error);
      }
    };

    fetchWilayas();
  }, []);

  useEffect(() => {
    if (user) {
      setFormData({
        phone: user.phone || '',
        address: user.address || '',
        wilaya: user.wilaya || ''
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const updatedUser = await response.json();
        updateUser(updatedUser);
        toast.success('Profil mis à jour');
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FCFBF9] flex items-center justify-center">
        <div className="text-center">
          <User size={48} className="text-[#E0DFDD] mx-auto mb-4" />
          <h1 className="font-serif text-2xl mb-4">Mon Compte</h1>
          <p className="text-[#6B6A68] mb-6">Connecte-toi pour accéder à ton compte</p>
          <button 
            onClick={login}
            className="btn-primary px-6 py-3 text-sm font-medium"
            data-testid="login-btn"
          >
            Se connecter avec Google
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FCFBF9]" data-testid="account-page">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <nav className="breadcrumb flex items-center gap-2 text-sm">
          <Link to="/" className="hover:text-[#1A1514]">Accueil</Link>
          <span>/</span>
          <span className="text-[#1A1514]">Mon Compte</span>
        </nav>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-3 gap-8">
          {/* Sidebar */}
          <div className="md:col-span-1">
            <div className="bg-white p-6 mb-6">
              <div className="flex items-center gap-4 mb-6">
                {user.picture ? (
                  <img 
                    src={user.picture} 
                    alt={user.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-16 h-16 bg-[#F4E3E1] rounded-full flex items-center justify-center">
                    <User size={24} className="text-[#D4AF37]" />
                  </div>
                )}
                <div>
                  <p className="font-medium">{user.name}</p>
                  <p className="text-sm text-[#6B6A68]">{user.email}</p>
                </div>
              </div>
            </div>

            <nav className="bg-white divide-y divide-[#E0DFDD]">
              <Link 
                to="/orders" 
                className="flex items-center justify-between p-4 hover:bg-[#F5F4F2] transition-colors"
                data-testid="nav-orders"
              >
                <div className="flex items-center gap-3">
                  <Package size={18} className="text-[#D4AF37]" />
                  <span>Mes Commandes</span>
                </div>
                <ChevronRight size={16} className="text-[#6B6A68]" />
              </Link>
              <Link 
                to="/wishlist" 
                className="flex items-center justify-between p-4 hover:bg-[#F5F4F2] transition-colors"
                data-testid="nav-wishlist"
              >
                <div className="flex items-center gap-3">
                  <Heart size={18} className="text-[#D4AF37]" />
                  <span>Ma Wishlist</span>
                </div>
                <ChevronRight size={16} className="text-[#6B6A68]" />
              </Link>
              <button 
                onClick={handleLogout}
                className="w-full flex items-center gap-3 p-4 hover:bg-[#F5F4F2] transition-colors text-[#C87979]"
                data-testid="logout-btn"
              >
                <LogOut size={18} />
                <span>Déconnexion</span>
              </button>
            </nav>
          </div>

          {/* Main Content */}
          <div className="md:col-span-2">
            <div className="bg-white p-6">
              <h2 className="font-serif text-xl mb-6">Informations personnelles</h2>
              
              <form onSubmit={handleSave} className="space-y-6">
                <div>
                  <label className="text-sm text-[#6B6A68] block mb-2">Nom</label>
                  <input
                    type="text"
                    value={user.name}
                    disabled
                    className="checkout-input bg-[#F5F4F2] text-[#6B6A68]"
                  />
                </div>

                <div>
                  <label className="text-sm text-[#6B6A68] block mb-2">Email</label>
                  <input
                    type="email"
                    value={user.email}
                    disabled
                    className="checkout-input bg-[#F5F4F2] text-[#6B6A68]"
                  />
                </div>

                <div>
                  <label className="text-sm text-[#6B6A68] block mb-2">Téléphone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="05xxxxxxxx"
                    className="checkout-input"
                    data-testid="input-phone"
                  />
                </div>

                <div>
                  <label className="text-sm text-[#6B6A68] block mb-2">Wilaya</label>
                  <select
                    name="wilaya"
                    value={formData.wilaya}
                    onChange={handleInputChange}
                    className="checkout-input bg-transparent"
                    data-testid="select-wilaya"
                  >
                    <option value="">Sélectionner une wilaya</option>
                    {wilayas.map((wilaya) => (
                      <option key={wilaya.code} value={wilaya.code}>
                        {wilaya.code} - {wilaya.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm text-[#6B6A68] block mb-2">Adresse</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    rows={3}
                    placeholder="Ton adresse complète..."
                    className="checkout-input resize-none"
                    data-testid="input-address"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary px-6 py-3 text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                  data-testid="save-profile-btn"
                >
                  {loading ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <Save size={18} />
                  )}
                  Enregistrer
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
